/* =========================================================
   WonderCases — Sibling hero preloader (per variant)
   ----------------------------------------------------
   Goal:
   - When a model/variant is selected on the current product,
     we "warm up" the hero images of the sibling products
     (Slim / Tough / MagSafe) for the same model.
   - This way, when the user clicks a sibling card, the hero
     image on that sibling PDP should appear much faster,
     because the large hero file is already in the browser
     cache.

   HOW TO USE:
   - Set SIBLING_CARD_SELECTOR below to match your case-type
     cards (e.g. '.wc-case-switcher-card' or
     '[data-wc-sibling-card]').
   - Each card must contain an <img> that shows the current
     model for that sibling. We derive the hero URL from that
     thumbnail URL by bumping its Shopify size suffix
     (e.g. _400x → _1200x).
   ========================================================= */
(function () {
  const root =
    document.getElementById("wc-product-root") ||
    document.querySelector('[data-section-type="product"]');

  if (!root) return;

// Each case-type card (Slim / Tough / MagSafe) wrapper link
const SIBLING_CARD_SELECTOR = "#wc-case-switcher .wc-case-card";


  // ---------- Helpers ----------

    // Detect the hero image width from the current product hero <img>
  // so we can request sibling images in the SAME width.
  let HERO_WIDTH = null;

  function detectHeroWidth() {
    if (HERO_WIDTH) return HERO_WIDTH;

    // Try to find the currently visible desktop hero image
    const heroImg =
      root.querySelector(
        '.above-mobile .product__media-item:not(.hidden) img'
      ) ||
      root.querySelector(
        '.above-mobile .product__media-item[data-media-item-id] img'
      );

    if (heroImg) {
      const src = heroImg.currentSrc || heroImg.src || "";
      const m = src && src.match(/width=(\d+)/);
      if (m && m[1]) {
        HERO_WIDTH = m[1]; // e.g. "1080"
      }
    }

    // Fallback if we cannot detect anything
    if (!HERO_WIDTH) {
      HERO_WIDTH = "1400";
    }

    return HERO_WIDTH;
  }

  // Try to upgrade a Shopify thumb URL to a hero-sized URL
  // using the same width as the current page hero.
  function toHeroUrl(url) {
    if (!url) return null;

    const heroWidth = detectHeroWidth(); // e.g. "1080"

    // 1) New CDN-style URLs: ?width=600 → ?width=1080 (hero width)
    if (url.indexOf("width=") !== -1) {
      return url.replace(/width=\d+/, "width=" + heroWidth);
    }

    // 2) Legacy suffix pattern: _400x.jpg → _1080x.jpg
    const match = url.match(/_(\d+)x\./);
    if (match) {
      return url.replace(/_(\d+)x\./, "_" + heroWidth + "x.");
    }

    // 3) If no size pattern is present, just return the original URL
    return url;
  }

  function collectSiblingHeroUrls() {
    const urls = new Set();

    const cards = root.querySelectorAll(SIBLING_CARD_SELECTOR);
    if (!cards.length) return [];

    cards.forEach((card) => {
      // Prefer an explicit data attribute if you add one later
      let src =
        card.getAttribute("data-wc-sibling-hero") ||
        null;

      // Fallback: use the first <img> inside the card
      if (!src) {
        const img = card.querySelector("img");
        if (img) {
          src = img.currentSrc || img.src || null;
        }
      }

      if (!src) return;

      const heroUrl = toHeroUrl(src);
      if (heroUrl) {
        urls.add(heroUrl);
      }
    });

    return Array.from(urls);
  }

  function preloadImages(urls) {
    urls.forEach((url) => {
      try {
        const img = new Image();
        img.decoding = "async";
        img.loading = "eager";
        img.src = url;
      } catch (e) {
        // Fail silently; this is only a perf optimization.
      }
    });
  }

  function warmUpSiblingHeroes() {
    const urls = collectSiblingHeroUrls();
    if (!urls.length) return;
    preloadImages(urls);
  }


  // ---------- Init + variant hooks ----------

  // Run once on initial load
  if (
    document.readyState === "complete" ||
    document.readyState === "interactive"
  ) {
    warmUpSiblingHeroes();
  } else {
    document.addEventListener("DOMContentLoaded", warmUpSiblingHeroes, {
      once: true,
    });
  }

  // Also run whenever the current variant changes,
  // so sibling thumbs already show the correct model
  // and we warm up those specific hero images.
  ["variant:change", "variant:changed", "product:variant-change"].forEach(
    (ev) => {
      document.addEventListener(ev, function () {
        // Small delay to let other variant scripts update
        window.requestAnimationFrame(warmUpSiblingHeroes);
      });
    }
  );
})();
